import { motion } from "framer-motion";
import { Instagram, Facebook, MessageCircle } from "lucide-react";
import { trackEvent } from "@/lib/api";

export default function Footer({ content, onNavigate }) {
  const social = content.social || {};
  const links = [
    { label: "Home", href: "#home", testid: "footer-nav-home" },
    { label: "About", href: "#about", testid: "footer-nav-about" },
    { label: "Apps", href: "#apps", testid: "footer-nav-apps" },
    { label: "Contact", href: "#contact", testid: "footer-nav-contact" },
  ];
  const socials = [
    { key: "instagram", label: "Instagram", icon: Instagram, href: social.instagram || "#", testid: "footer-instagram-link" },
    { key: "facebook", label: "Facebook", icon: Facebook, href: social.facebook || "#", testid: "footer-facebook-link" },
    { key: "whatsapp", label: "WhatsApp Channel", icon: MessageCircle, href: social.whatsapp_channel || "#", testid: "footer-whatsapp-link" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-blood text-ivory overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 md:pt-28">
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "70%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-medium tracking-[0.18em] leading-none text-[5.5vw] lg:text-[4.5vw] text-goldlight"
            data-testid="footer-kannur-line"
          >
            KANNUR
          </motion.p>
          <motion.h2
            initial={{ y: "60%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-semibold tracking-tighter leading-[0.95] text-[11vw] lg:text-[9vw] whitespace-nowrap"
            data-testid="footer-wordmark"
          >
            THEYYAM<span className="text-gold">NOW</span>
          </motion.h2>
        </div>

        <div className="mt-14 md:mt-20 border-t border-ivory/20 pt-12 flex flex-wrap items-end justify-between gap-10">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs uppercase tracking-[0.35em] text-goldlight/80 font-medium" data-testid="footer-social-overline">
              Stay Connected
            </p>
            <h3 className="mt-4 font-serif font-medium tracking-tight text-3xl md:text-5xl" data-testid="footer-follow-heading">
              Follow the fire.
            </h3>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap items-center gap-3"
            data-testid="footer-socials"
          >
            {socials.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent(
                      { instagram: "instagram_click", facebook: "facebook_click", whatsapp: "whatsapp_channel" }[s.key],
                      { button_name: s.label, destination_url: s.href }
                    )
                  }
                  data-testid={s.testid}
                  className="inline-flex items-center gap-3 border border-ivory/30 px-4 py-3.5 sm:px-5 text-xs uppercase tracking-[0.2em] text-ivory/85 transition-colors duration-300 hover:bg-ivory hover:text-blood hover:border-ivory"
                  aria-label={s.label}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span className="hidden sm:inline">{s.label}</span>
                </a>
              );
            })}
          </motion.div>
        </div>

        <div className="mt-12 border-t border-ivory/20 py-8 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Kannur TheyyamNow" className="h-9 w-auto" />
            <p className="text-xs sm:text-sm text-ivory/70 whitespace-nowrap" data-testid="footer-copyright">
              {content.footer_text || "© Kannur TheyyamNow. All rights reserved."}
            </p>
          </div>

          <nav className="flex items-center gap-8" data-testid="footer-nav">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => onNavigate(l.href)}
                data-testid={l.testid}
                className="text-xs uppercase tracking-[0.22em] text-ivory/70 hover:text-goldlight transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}
